// Third-party
import moment from "moment";
import isNull from "lodash-es/isNull";

// Types
import { ICommissionCycle } from "@/Common/api/types/CommissionCycle";

// Constants
import {
	FIFTY,
	EIGHTY,
	ONE_HUNDRED_EIGHTY,
	ONE_HUNDRED_TEN,
	ONE_HUNDRED_TWENTY,
	ONE_HUNDRED,
	ONE_HUNDRED_THIRTY_THREE,
	ONE_HUNDRED_FORTY,
	TWO_HUNDRED,
} from "@/Constants/tables";
import { INITIAL_RULE, INITIAL_TIER, INITIAL_TIER_DEP, INITIAL_TIER_OP_CODES } from "../components/EligibilityRequirementsDialog/reducer";
import isEqual from "lodash-es/isEqual";
import { ESpiffTypes, IEligibilityRequirement, ISpiffSetupRule, ITiers } from "../types/spiffSetup";
import { emptyValidation } from "@/Layouts/components/Tables/DataSmartTable/validation/TableValidations";
import { IVariableOperationsGpsBonusSetup } from "@/PayPlans/types/sections/variableOperationsGpsBonus";
import { allowedGPSKeys, filterByInterface, hasPositiveBonus } from "../components/EligibilityRequirementsDialog/utils";
import { IOpCode } from "@/PayPlans/types/sections/technicians";

const categorizeDays = (daysString: string[]): string => {
	const ALL_DAYS_OF_WEEK = 7;
	const ALL_WEEKDAYS = 5;
	const ALL_WEEKENDS = 2;
	const ONE_DAY = 1;

	// Convert the input string to lowercase and split it into an array
	const days = daysString.map((day) => day.toLowerCase().trim());

	// Define sets for weekdays and weekend days
	const weekdays = new Set(["monday", "tuesday", "wednesday", "thursday", "friday"]);
	const weekendDays = new Set(["saturday", "sunday"]);

	// Check if all days are valid
	const allDaysValid = days.every((day) => weekdays.has(day) || weekendDays.has(day));
	if (!allDaysValid) {
		throw new Error("Invalid day(s) provided");
	}

	// Check for special cases
	if (days.length === ALL_DAYS_OF_WEEK) {
		return "Everyday";
	}
	if (days.length === ONE_DAY) {
		return days.map(capitalizeFirstLetter).join("");
	}

	// Check if all days are weekdays or all are weekend days
	const allWeekdays = days.every((day) => weekdays.has(day));
	const allWeekends = days.every((day) => weekendDays.has(day));

	if (allWeekdays && days.length === ALL_WEEKDAYS) {
		return "Weekday(s)";
	}
	if (allWeekends && days.length === ALL_WEEKENDS) {
		return "Weekend(s)";
	}

	// If we reach here, return the actual days
	return days.map(capitalizeFirstLetter).join(", ");
};

// Helper function to capitalize the first letter of a string
const capitalizeFirstLetter = (string: string): string => {
	return string.charAt(0).toUpperCase() + string.slice(1);
};

const isDateRangeIntersecting = ({
	commissionCycleStart,
	commissionCycleEnd,
	definitionStart,
	definitionEnd,
}: {
	commissionCycleStart: moment.Moment;
	commissionCycleEnd: moment.Moment;
	definitionStart: moment.Moment;
	definitionEnd: moment.Moment;
}): boolean => {
	return definitionStart.isSameOrBefore(commissionCycleEnd) && definitionEnd.isSameOrAfter(commissionCycleStart);
};

interface Award {
	spiffDate: string;
}
interface Awards {
	awards: Award[];
}
interface IRow {
	createdAt: string;
	effectiveDateRangeFrom: string;
	effectiveDateRangeTo: string;
	isNew: boolean;
	awards: Awards[];
}

const removeOutOfDateAwards = ({
	awards,
	commissionCycleStart,
	commissionCycleEnd,
}: {
	awards: Awards[];
	commissionCycleStart: moment.Moment;
	commissionCycleEnd: moment.Moment;
}) => {
	const result: Awards[] = [];
	if (!awards) {
		return result;
	}
	for (const award of awards) {
		const innerAwards = award.awards.filter(
			(innerAward: Award) =>
				moment.utc(innerAward.spiffDate).isSameOrBefore(commissionCycleEnd) &&
				moment.utc(innerAward.spiffDate).isSameOrAfter(commissionCycleStart)
		);
		result.push({
			...award,
			awards: innerAwards,
		});
	}
	return result;
};

const getRows = (allRows: IRow[], commissionCycleObj: ICommissionCycle) => {
	if (!allRows || !commissionCycleObj) {
		return [];
	}

	return allRows
		.filter((row) => {
			return (
				isDateRangeIntersecting({
					commissionCycleStart: moment.utc(commissionCycleObj.startDate),
					commissionCycleEnd: moment.utc(commissionCycleObj.endDate),
					definitionStart: moment.utc(row.effectiveDateRangeFrom).startOf("day"),
					definitionEnd: moment.utc(row.effectiveDateRangeTo).endOf("day"),
				}) || row.isNew
			);
		})
		.map((row) => {
			row.awards = removeOutOfDateAwards({
				awards: row.awards,
				commissionCycleStart: moment.utc(commissionCycleObj.startDate),
				commissionCycleEnd: moment.utc(commissionCycleObj.endDate),
			});
			return row;
		});
};

const definedRowByOptions = (motivatorType: string, optionA: number, optionB: number) =>
	motivatorType === "spiffSetup" ? optionA : optionB;

const defineWidthColumns = (motivatorType: string) => {
	const widthColumnMap = {
		id: FIFTY,
		createdAt: FIFTY,
		title: ONE_HUNDRED,
		spiffType: ONE_HUNDRED_TEN,
		eligibility: definedRowByOptions(motivatorType, ONE_HUNDRED_FORTY, TWO_HUNDRED),
		positions: ONE_HUNDRED_EIGHTY,
		effectiveDateRangeFrom: definedRowByOptions(motivatorType, ONE_HUNDRED_THIRTY_THREE, ONE_HUNDRED_EIGHTY),
		effectiveDateRangeTo: definedRowByOptions(motivatorType, ONE_HUNDRED_THIRTY_THREE, ONE_HUNDRED_EIGHTY),
		eligibilityDays: ONE_HUNDRED_THIRTY_THREE,
		requiredDealStatus: ONE_HUNDRED_TWENTY,
		transactionType: EIGHTY,
		reward: ONE_HUNDRED_TEN,
		actions: 68,
		type: ONE_HUNDRED,
		description: ONE_HUNDRED_TWENTY,
	};

	const spiffSetupColumns = [
		{ name: "_id", width: widthColumnMap["id"] },
		{ name: "createdAt", width: widthColumnMap["createdAt"] },
		{ name: "title", width: widthColumnMap["title"] },
		{ name: "spiffType", width: widthColumnMap["spiffType"] },
		{ name: "eligibility", width: widthColumnMap["eligibility"] },
		{ name: "positions", width: widthColumnMap["positions"] },
		{ name: "effectiveDateRangeFrom", width: widthColumnMap["effectiveDateRangeFrom"] },
		{ name: "effectiveDateRangeTo", width: widthColumnMap["effectiveDateRangeTo"] },
		{ name: "eligibilityDays", width: widthColumnMap["eligibilityDays"] },
		{ name: "requiredDealStatus", width: widthColumnMap["requiredDealStatus"] },
		{ name: "reward", width: widthColumnMap["reward"] },
		{ name: "actions", width: widthColumnMap["actions"] },
	];
	const spiffBonusColumns = [
		{ name: "_id", width: widthColumnMap["id"] },
		{ name: "createdAt", width: widthColumnMap["createdAt"] },
		{ name: "title", width: widthColumnMap["title"] },
		{ name: "spiffType", width: widthColumnMap["spiffType"] },
		{ name: "eligibility", width: widthColumnMap["eligibility"] },
		{ name: "positions", width: widthColumnMap["positions"] },
		{ name: "effectiveDateRangeFrom", width: widthColumnMap["effectiveDateRangeFrom"] },
		{ name: "effectiveDateRangeTo", width: widthColumnMap["effectiveDateRangeTo"] },
		{ name: "eligibilityDays", width: widthColumnMap["eligibilityDays"] },
		{ name: "transactionType", width: widthColumnMap["transactionType"] },
		{ name: "actions", width: widthColumnMap["actions"] },
	];

	return { spiffSetupColumns, spiffBonusColumns };
};

const isInValidTier = (tier: ITiers) => {
	const { commissions } = tier;
	return (
		commissions?.length === 1 &&
		(commissions[0].commission?.value === undefined ||
			commissions[0].commission?.value === INITIAL_TIER.commissions[0].commission.value)
	);
};

const checkTierError = (tiers: ITiers[]) => {
	if (tiers?.length === 0) {
		return true;
	}
	if (tiers?.length === 1 && (isEqual(tiers[0], INITIAL_TIER) || isInValidTier(tiers[0]))) {
		return true;
	}
	return false;
};

const checkRuleError = (rules: ISpiffSetupRule[] | undefined) => {
	const isRulesEmpty = rules?.length === 0;
	const isInitialRule = rules?.some((rule) => isEqual(rule, INITIAL_RULE));
	return isRulesEmpty || isInitialRule;
};

const checkGenericError = (genericValue: string | undefined) => {
	const valueExists = emptyValidation(genericValue);
	// Allow only alphanumeric characters and spaces
	const regexEligibility = /^[a-zA-Z0-9].*$/;
	const passedValidation = regexEligibility.test(genericValue ?? "");
	return !valueExists || !passedValidation;
};

const checkFranchisesError = (franchises: string[] | undefined) => {
	return franchises?.length === 0;
};

const isRequirementsInError = (
	state: IEligibilityRequirement | IVariableOperationsGpsBonusSetup,
	spiffType: string,
	extraErrors: Map<any, any> | null
) => {
	if (spiffType === ESpiffTypes.GENERIC) {
		return checkGenericError((state as IEligibilityRequirement)?.genericValue);
	}
	if (spiffType === ESpiffTypes.TRADES) {
		return (
			checkTierError((state as IEligibilityRequirement)?.tiers) ||
			checkFranchisesError((state as IEligibilityRequirement)?.franchises)
		);
	}
	if (spiffType === ESpiffTypes.SALES) {
		return checkTierError((state as IEligibilityRequirement)?.tiers) || checkRuleError((state as IEligibilityRequirement)?.rules);
	}
	if (spiffType === ESpiffTypes.GPS_COMPLIANCE) {
		const filteredState = filterByInterface(state, allowedGPSKeys);
		const atLeastOnePositiveBonus = hasPositiveBonus(filteredState as IVariableOperationsGpsBonusSetup);
		if (!!extraErrors?.get("variableOperationsGpsBonus")) {
			return true;
		}
		if (isNull(extraErrors) && !atLeastOnePositiveBonus) {
			return true;
		}
		return false;
	}
	if (spiffType === ESpiffTypes.DEPARTMENT) {
		return (
			checkTargetedBonusDepTierError((state as IEligibilityRequirement)?.tiers) ||
			checkPayType((state as IEligibilityRequirement)?.selectedPayType) ||
			checkSelectedDepartment((state as IEligibilityRequirement)?.selectedDepartment)
		);
	}
	if (spiffType === ESpiffTypes.OP_CODES) {
		return (
			checkTargetedBonusOpCodesTierError((state as IEligibilityRequirement)?.tiers) ||
			checkPayType((state as IEligibilityRequirement)?.selectedPayType) ||
			checkSelectedOperationCodes((state as IEligibilityRequirement)?.selectedOperationCodes) ||
			checkSelectedTimeLapse((state as IEligibilityRequirement)?.selectedTimeLapse)
		);
	}
	return false;
};

const checkTargetedBonusDepTierError = (tiers: ITiers[]) => {
	if (tiers?.length === 0) {
		return true;
	}
	if (tiers?.length === 1 && isEqual(tiers[0], INITIAL_TIER_DEP)) {
		return true;
	}
	return false;
};

const checkTargetedBonusOpCodesTierError = (tiers: ITiers[]) => {
	if (tiers?.length === 0) {
		return true;
	}
	if (tiers?.length === 1 && isEqual(tiers[0], INITIAL_TIER_OP_CODES)) {
		return true;
	}
	return false;
};

const checkPayType = (selectedPayType: string[] | undefined) => {
	return !Boolean(selectedPayType?.length);
};

const checkSelectedDepartment = (selectedDepartment: string[] | undefined) => {
	return !Boolean(selectedDepartment?.length);
};

const checkSelectedOperationCodes = (selectedOperationCodes: IOpCode[] | undefined) => {
	return !Boolean(selectedOperationCodes?.length);
};

const checkSelectedTimeLapse = (selectedTimeLapse: string | undefined) => {
	return !Boolean(selectedTimeLapse);
};

const defineSpiffOptions = () => {
	return {
		spiffSetupOptions: [{ label: "Generic" }, { label: "Trades" }, { label: "Sales" }, { label: "GPS Compliance" }],
		spiffBonusOptions: [{ label: "Generic" }, { label: "Op Codes" }, { label: "Department" }],
	};
};

export {
	getRows,
	defineWidthColumns,
	categorizeDays,
	checkTierError,
	checkRuleError,
	checkGenericError,
	isRequirementsInError,
	checkFranchisesError,
	checkTargetedBonusDepTierError,
	checkTargetedBonusOpCodesTierError,
	checkPayType,
	checkSelectedDepartment,
	checkSelectedOperationCodes,
	checkSelectedTimeLapse,
	defineSpiffOptions,
};
